import React, {useEffect, useState} from 'react'
import Form from "../components/Form/Form";
import {useForm, FormProvider} from "react-hook-form";
import FormContainer from '../components/Form/FormContainer';
import Button from 'react-bootstrap/Button';
import UiInputSuggest from "../components/Form/UiInputSuggest";
import { get, post } from "../services/ApiService";
import { Alert, Table, Badge } from 'react-bootstrap';

interface Recebimento {
    id: number;
    data: string;
    valor: number;
    origem: string;
    descricao: string;
    saldo_livre: number;
    valor_alocado_ar: number;
}

export default function ConciliacaoForm(props: any){
    const methods = useForm({
        defaultValues: props.data || {}
    });
    const { handleSubmit, watch, setValue } = methods;

    const [recebimentos, setRecebimentos] = useState<Recebimento[]>([]);
    const [alocacoes, setAlocacoes] = useState<{[key: number]: number}>({});
    const [loading, setLoading] = useState(false);
    const [mensagem, setMensagem] = useState<{tipo: string, texto: string} | null>(null);

    const contaReceber = watch('contaReceber');
    const arValorTotal = props.data?.valor_total || contaReceber?.valorTotal || 0;
    const arId = props.data?.id || contaReceber?.id;

    useEffect(() => {
        if (arId && contaReceber?.contrato?.id) {
            carregarRecebimentos(contaReceber.contrato.id, arId);
        }
    }, [arId, contaReceber?.contrato?.id]);

    const carregarRecebimentos = async (contratoId: number, arId: number) => {
        setLoading(true);
        try {
            const response = await get(`/?route=arAlocacao/form&action=recebimentosDisponiveis&contratoId=${contratoId}&arId=${arId}`);
            if (response.data?.success) {
                const recs = response.data.data || [];
                setRecebimentos(recs);

                const alocInicial: {[key: number]: number} = {};
                recs.forEach((r: Recebimento) => {
                    if (r.valor_alocado_ar > 0) {
                        alocInicial[r.id] = parseFloat(String(r.valor_alocado_ar));
                    }
                });
                setAlocacoes(alocInicial);
            }
        } catch (error) {
            console.error('Erro ao carregar recebimentos:', error);
        }
        setLoading(false);
    };

    const handleAlocacaoChange = (recId: number, valor: number) => {
        setAlocacoes(prev => ({
            ...prev,
            [recId]: valor
        }));
    };

    const totalAlocado = Object.values(alocacoes).reduce((sum, val) => sum + (val || 0), 0);
    const saldoAberto = parseFloat(String(arValorTotal)) - totalAlocado;

    const onSubmit = async () => {
        if (!arId) {
            setMensagem({ tipo: 'danger', texto: 'Selecione uma Conta a Receber' });
            return;
        }

        setLoading(true);
        setMensagem(null);

        try {
            const alocacoesArray = Object.entries(alocacoes)
                .filter(([_, valor]) => valor > 0)
                .map(([recId, valor]) => ({
                    recebimentoId: parseInt(recId),
                    valor: valor
                }));

            const response = await post('/?route=arAlocacao/form&action=conciliar', {
                dados: {
                    arId: arId,
                    alocacoes: alocacoesArray
                }
            });

            if (response.data?.success) {
                setMensagem({ tipo: 'success', texto: response.data.message || 'Conciliação realizada com sucesso!' });
                setTimeout(() => {
                    props.triggerClose();
                    if (props.onSuccess) props.onSuccess();
                }, 1500);
            } else {
                setMensagem({ tipo: 'danger', texto: response.data?.message || 'Erro ao conciliar' });
            }
        } catch (error: any) {
            setMensagem({ tipo: 'danger', texto: error.message || 'Erro ao conciliar' });
        }

        setLoading(false);
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('pt-BR');
    };

    return (
        <FormContainer size="xl" show={true} onHide={props.triggerClose}>
            <FormProvider {...methods}>
                <Form onSubmit={handleSubmit(onSubmit)} methods={methods}>
                    <FormContainer.Header onClose={props.triggerClose}>
                        <h5>Conciliação de Recebimentos</h5>
                    </FormContainer.Header>
                    <FormContainer.Body>
                        {mensagem && (
                            <Alert variant={mensagem.tipo} onClose={() => setMensagem(null)} dismissible>
                                {mensagem.texto}
                            </Alert>
                        )}

                        <div className="row mb-4">
                            <div className="col-12">
                                <fieldset>
                                    <legend>Conta a Receber</legend>
                                    {props.data?.id ? (
                                        <div className="row">
                                            <div className="col-2"><strong>AR #:</strong> {props.data.id}</div>
                                            <div className="col-4"><strong>Descrição:</strong> {props.data.descricao}</div>
                                            <div className="col-3"><strong>Valor Total:</strong> {formatCurrency(arValorTotal)}</div>
                                            <div className="col-3"><strong>Contrato:</strong> #{props.data.contrato_id}</div>
                                        </div>
                                    ) : (
                                        <div className="row">
                                            <UiInputSuggest size={12} router="contaReceber" idField="id" titleField="descricao" name="contaReceber" label="Selecione a Conta a Receber" />
                                        </div>
                                    )}
                                </fieldset>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-4 text-center">
                                <div className="p-3 bg-light rounded">
                                    <small className="text-muted">Valor Total AR</small>
                                    <h4>{formatCurrency(arValorTotal)}</h4>
                                </div>
                            </div>
                            <div className="col-4 text-center">
                                <div className="p-3 bg-light rounded">
                                    <small className="text-muted">Total Alocado</small>
                                    <h4 className="text-primary">{formatCurrency(totalAlocado)}</h4>
                                </div>
                            </div>
                            <div className="col-4 text-center">
                                <div className={`p-3 rounded ${saldoAberto <= 0 ? 'bg-success text-white' : 'bg-warning'}`}>
                                    <small>Saldo Aberto</small>
                                    <h4>{formatCurrency(saldoAberto)}</h4>
                                </div>
                            </div>
                        </div>

                        <fieldset>
                            <legend>Recebimentos Disponíveis</legend>
                            {loading ? (
                                <div className="text-center p-4">Carregando...</div>
                            ) : recebimentos.length === 0 ? (
                                <Alert variant="info">
                                    Nenhum recebimento disponível para este contrato.
                                    {!arId && ' Selecione uma Conta a Receber primeiro.'}
                                </Alert>
                            ) : (
                                <Table striped bordered hover size="sm">
                                    <thead>
                                        <tr>
                                            <th style={{width: '80px'}}>ID</th>
                                            <th style={{width: '100px'}}>Data</th>
                                            <th style={{width: '100px'}}>Origem</th>
                                            <th>Descrição</th>
                                            <th style={{width: '120px'}}>Valor Rec.</th>
                                            <th style={{width: '120px'}}>Saldo Livre</th>
                                            <th style={{width: '150px'}}>Alocar</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recebimentos.map((rec) => (
                                            <tr key={rec.id}>
                                                <td>#{rec.id}</td>
                                                <td>{formatDate(rec.data)}</td>
                                                <td>
                                                    <Badge bg="secondary">{rec.origem?.toUpperCase()}</Badge>
                                                </td>
                                                <td>{rec.descricao || '-'}</td>
                                                <td className="text-end">{formatCurrency(rec.valor)}</td>
                                                <td className="text-end">{formatCurrency(rec.saldo_livre)}</td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        value={alocacoes[rec.id] || ''}
                                                        onChange={(e) => handleAlocacaoChange(rec.id, parseFloat(e.target.value) || 0)}
                                                        max={rec.saldo_livre + (rec.valor_alocado_ar || 0)}
                                                        min={0}
                                                        step="0.01"
                                                        placeholder="0,00"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </fieldset>
                    </FormContainer.Body>
                    <FormContainer.Footer>
                        <Button variant="secondary" onClick={props.triggerClose}>Fechar</Button>
                        <Button variant="primary" type="submit" disabled={loading || !arId}>
                            {loading ? 'Salvando...' : 'Salvar Conciliação'}
                        </Button>
                    </FormContainer.Footer>
                </Form>
            </FormProvider>
        </FormContainer>
    )
}
